import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  artworkBox() { return this.querySelector('[data-artwork-designer]'); } artworkMessage(message) { const box = this.artworkBox(); if (!box) return; const error = box.querySelector('[data-artwork-error]'); error.textContent = message; error.hidden = !message; box.querySelector('[data-artwork-choose]').setAttribute('aria-invalid', message ? 'true' : 'false'); } chooseArtwork() { this.querySelector('[data-frogigo-artwork]')?.click(); } removeArtwork() { const box = this.artworkBox(); if (!box) return; this.artworkVersion = (this.artworkVersion || 0) + 1; this.artworkImage = null; this.artworkFile = null; this.artworkLoading = false; if (this.artworkURL) URL.revokeObjectURL(this.artworkURL); this.artworkURL = ''; const input = box.querySelector('[data-frogigo-artwork]'); input.value = ''; input.setCustomValidity(''); box.querySelector('[data-artwork-name]').textContent = box.dataset.empty; box.querySelector('[data-artwork-choose]').textContent = box.dataset.choose; box.querySelector('[data-artwork-remove]').hidden = true; box.querySelector('[data-artwork-editor]').hidden = true; box.querySelector('[data-artwork-status]').textContent = ''; box.querySelector('[data-artwork-layout]').value = ''; box.querySelector('[data-artwork-json]').value = ''; const canvas = box.querySelector('canvas'); canvas.getContext('2d').clearRect(0,0,canvas.width,canvas.height); this.artworkMessage(''); } async loadArtwork() { const box = this.artworkBox(); if (!box) return this.validateArtwork(); const input = box.querySelector('[data-frogigo-artwork]'); const file = input.files?.[0]; this.artworkVersion = (this.artworkVersion || 0) + 1; const version = this.artworkVersion; this.artworkImage = null; this.artworkFile = null; this.artworkLoading = false; if (this.artworkURL) URL.revokeObjectURL(this.artworkURL); this.artworkURL = ''; box.querySelector('[data-artwork-editor]').hidden = true; box.querySelector('[data-artwork-layout]').value = ''; box.querySelector('[data-artwork-json]').value = ''; box.querySelector('[data-artwork-status]').textContent = ''; box.querySelector('[data-artwork-name]').textContent = file ? file.name : box.dataset.empty; box.querySelector('[data-artwork-choose]').textContent = file ? box.dataset.replace : box.dataset.choose; box.querySelector('[data-artwork-remove]').hidden = !file; this.artworkMessage(''); if (!file) return; if (!/\.(png|jpe?g)$/i.test(file.name) || !['image/png','image/jpeg'].includes(file.type) || file.size <= 0 || file.size > 20*1024*1024) { this.artworkMessage(input.dataset.invalidMessage); return; } this.artworkLoading = true; box.querySelector('[data-artwork-status]').textContent = box.dataset.loading; const url = URL.createObjectURL(file); this.artworkURL = url; const image = new Image(); image.src = url; try { await image.decode(); if (version !== this.artworkVersion || !this.isConnected) return; if (!image.naturalWidth || !image.naturalHeight) throw new Error('Invalid image dimensions'); this.artworkImage = image; this.artworkFile = file; this.artworkLoading = false; box.querySelector('[data-artwork-editor]').hidden = false; this.resetArtwork(); box.querySelector('[data-artwork-status]').textContent = box.dataset.ready; } catch (error) { if (version !== this.artworkVersion) return; this.artworkLoading = false; box.querySelector('[data-artwork-status]').textContent = ''; this.artworkMessage(box.dataset.decode); } finally { URL.revokeObjectURL(url); if (this.artworkURL === url) this.artworkURL = ''; } } resetArtwork() { const box = this.artworkBox(); if (!box) return; box.querySelector('[data-artwork-mode]').value = 'fill'; box.querySelector('[data-artwork-zoom]').value = '100'; box.querySelector('[data-artwork-x]').value = '50'; box.querySelector('[data-artwork-y]').value = '50'; this.updateArtwork(); } updateArtwork() { const box = this.artworkBox(); const image = this.artworkImage; if (!box || !image) return; const canvas = box.querySelector('canvas'); const ctx = canvas.getContext('2d'); if (!ctx) return; const mode = box.querySelector('[data-artwork-mode]').value; const zoom = Number(box.querySelector('[data-artwork-zoom]').value)/100; const px = Number(box.querySelector('[data-artwork-x]').value)/100; const py = Number(box.querySelector('[data-artwork-y]').value)/100; const iw = image.naturalWidth, ih = image.naturalHeight; const scale = (mode === 'fit' ? Math.min : Math.max)(canvas.width/iw, canvas.height/ih)*zoom; const w = iw*scale, h = ih*scale; const x = (canvas.width-w)*px, y = (canvas.height-h)*py; ctx.fillStyle = '#ffffff'; ctx.fillRect(0,0,canvas.width,canvas.height); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'; ctx.drawImage(image,x,y,w,h); const round = n => Math.round(n*1000000)/1000000; const layout = {version:1,area_inches:[24,14],source_pixels:[iw,ih],mode,zoom:round(zoom),placement:{x:round(x/canvas.width),y:round(y/canvas.height),width:round(w/canvas.width),height:round(h/canvas.height)},background:'#ffffff'}; box.querySelector('[data-artwork-json]').value = JSON.stringify(layout); box.querySelector('[data-artwork-layout]').value = '24 x 14 in; '+mode+'; zoom '+Math.round(zoom*100)+'%; horizontal '+Math.round(px*100)+'%; vertical '+Math.round(py*100)+'%'; box.querySelector('[data-artwork-zoom-value]').textContent = Math.round(zoom*100)+'%'; box.querySelector('[data-artwork-x-value]').textContent = Math.round(px*100)+'%'; box.querySelector('[data-artwork-y-value]').textContent = Math.round(py*100)+'%'; box.querySelector('[data-artwork-resolution]').textContent = iw+' × '+ih+' px'; box.querySelector('[data-artwork-low]').hidden = iw/(w/canvas.width*24) >= 150; } validateArtwork() { const input = this.querySelector('input[data-frogigo-artwork]'); if (!(input instanceof HTMLInputElement)) return true; const file = input.files?.[0]; const valid = file && /\.(png|jpe?g)$/i.test(file.name) && ['image/png','image/jpeg'].includes(file.type) && file.size > 0 && file.size <= 20*1024*1024; const box = this.artworkBox(); if (!box) { input.setCustomValidity(!file ? input.dataset.requiredMessage : valid ? '' : input.dataset.invalidMessage); return input.validity.valid; } let message = !file ? input.dataset.requiredMessage : !valid ? input.dataset.invalidMessage : this.artworkLoading ? box.dataset.wait : (!this.artworkImage || this.artworkFile !== file) ? box.dataset.decode : ''; this.artworkMessage(message); if (message) box.querySelector('[data-artwork-choose]').focus(); return !message; } appendArtworkPreview(formData) { const box = this.artworkBox(); if (!box) return; this.updateArtwork(); const data = box.querySelector('canvas').toDataURL('image/jpeg',0.92).split(',')[1]; const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0)); formData.set('properties[Layout preview]', new Blob([bytes],{type:'image/jpeg'}),'frogigo-layout-preview.jpg'); formData.set('properties[Print layout]',box.querySelector('[data-artwork-layout]').value); formData.set('properties[_Artwork layout]',box.querySelector('[data-artwork-json]').value); } handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (!this.validateArtwork() || !form.reportValidity()) return; const formData = new FormData(form); try { this.appendArtworkPreview(formData); } catch (error) { const box = this.artworkBox(); this.artworkMessage(box?.dataset.decode || ''); return; }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
